import { inject, Injectable } from '@angular/core';
import { AbstractControl, AsyncValidator } from '@angular/forms';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { MasterDataService } from '../../../../services/master-data.service';
import { Equipment } from '../../../../models/equipment.model';
import { BulkWorkOrderSharedDataService } from '../../../../services/bulk-work-order-shared-data.service';
import { Mode } from '../../../../models/mode.model';
import { Shop } from '../../../../models/shop.model';
import { ToastrService } from '../../../../services/toastr-service';
import { ApplicationtTextService } from '../../../../services/application-text.service';
import { AemsValidationError } from '../../../../models/aems-validation-error.model';

@Injectable({ providedIn: 'root' })
export class EquipmentDetailsValidator implements AsyncValidator {
  private masterDataService: MasterDataService;
  private sharedDataService: BulkWorkOrderSharedDataService;
  private toastrService: ToastrService;
  private appTextSvc: ApplicationtTextService;
  private selectedMode: Mode | null = null;
  private selectedShop: Shop | null = null;
  constructor() {
    this.masterDataService = inject(MasterDataService);
    this.sharedDataService = inject(BulkWorkOrderSharedDataService);
    this.toastrService = inject(ToastrService);
    this.appTextSvc = inject(ApplicationtTextService);
    this.sharedDataService.Mode.subscribe((mode) => (this.selectedMode = mode));
    this.sharedDataService.Shop.subscribe((shop) => (this.selectedShop = shop));
  }
  validate(control: AbstractControl): Observable<AemsValidationError | null> {
    if (control.value) {
      return this.masterDataService.getEquipmentDetails(control.value, '').pipe(
        map((res) => {
          this.shopAndEquipmentLocationMismatch(res);
          return (
            this.validateEquipmentExist(res) ||
            this.validateModeAndEquipmentType(res) ||
            this.validateEquipmentSize(res) ||
            this.validateExpiredLeaseAgreement(res) ||
            this.validateLeaseAgreementForSpecificEquipment(res) ||
            this.validateInvalidEquipmentTypes(res) ||
            this.validateRedeliveryContainers(res)
          );
        }),
        catchError((err) => of({ equipmentDetails: { error: this.appTextSvc.EquipmentNotInComet } })),
      );
    } else {
      return of(null);
    }
  }

  private validateEquipmentExist(equipment: Equipment): AemsValidationError | null {
    if (!equipment) {
      return { equipmentDetails: { error: this.appTextSvc.EquipmentNotInComet } };
    }
    this.sharedDataService.AddValidatedEquipment = equipment;
    return null;
  }

  private validateModeAndEquipmentType(equipment: Equipment): AemsValidationError | null {
    if (this.selectedMode?.code === '03' && (equipment?.equipmentTypeCode === 'REEF' || equipment?.equipmentTypeParentCode === 'GENS')) {
      return { equipmentDetails: { error: this.appTextSvc.OnlyDryContainerAllowed } };
    }
    if (
      (this.selectedMode?.code === '43' || this.selectedMode?.code === '02') &&
      (equipment?.equipmentTypeCode === 'DRY' || equipment?.equipmentTypeParentCode === 'GENS')
    ) {
      return { equipmentDetails: { error: this.appTextSvc.OnlyReeferContainerAllowed } };
    }
    if (this.selectedMode?.code === '33' && equipment?.equipmentTypeParentCode === 'CONT') {
      return { equipmentDetails: { error: this.appTextSvc.OnlyGensetAllowed } };
    }
    return null;
  }

  private shopAndEquipmentLocationMismatch(equipment: Equipment): null {
    if (equipment.presentLocation !== this.selectedShop?.locationCode) {
      this.toastrService.showToastr(this.appTextSvc.LocationMismatchWarning, 'warning', { duration: 60000 });
    }
    return null;
  }

  /**
   * If ownership type for the equipment is Vessel Sharing Agreement (VSA) or Shipper Owned (SO)
   * then repair is not allowed, irrespective of agreement type.
   * @param equipment
   * @param control
   * @returns
   */
  private validateExpiredLeaseAgreement(equipment: Equipment): AemsValidationError | null {
    const ownershipTypeCodes: string[] = ['VSA', 'SO'];
    if (equipment?.equipmentTypeParentCode === 'CONT' && ownershipTypeCodes.includes(equipment.ownershipTypeCode ?? '')) {
      return { equipmentDetails: { error: this.appTextSvc.EquipmentTypeNotAllowed } };
    }
    return null;
  }

  /**
   * For container equipment types other than 'GENS' and 'CHAS', if the ownership type is Vessel Sharing Agreement (VSA) or Shipper Owned (SO),
   * or if there is lease agreement comment, repair is not allowed.
   * @param equipment
   * @param control
   * @returns
   */
  private validateLeaseAgreementForSpecificEquipment(equipment: Equipment): AemsValidationError | null {
    const ownershipTypeCodes: string[] = ['VSA', 'SO'];
    const specificEquipmentTypes: string[] = ['GENS', 'CHAS'];

    const isSpecificEquipmentType = specificEquipmentTypes.includes(equipment?.equipmentTypeCode ?? '');
    const hasLeaseAgreementComment = !!equipment.positionLeaseAgreementComment;
    const hasLeaseAgreementOwnershipType = ownershipTypeCodes.includes(equipment?.ownershipTypeCode ?? '');

    if (!isSpecificEquipmentType && (hasLeaseAgreementComment || hasLeaseAgreementOwnershipType)) {
      return { equipmentDetails: { error: this.appTextSvc.EquipmentLeaseValidation } };
    }

    return null;
  }

  /**
   * Allow only Containers
   * @param equipment
   * @param control
   * @returns
   */
  private validateInvalidEquipmentTypes(equipment: Equipment): AemsValidationError | null {
    const allowedParentCodes = ['CONT', 'GENS'];
    const isAllowedType = allowedParentCodes.includes(equipment.equipmentTypeParentCode ?? '');
    if (!isAllowedType) {
      return { equipmentDetails: { error: this.appTextSvc.OnlyContainersAllowed } };
    }
    return null;
  }

  private validateRedeliveryContainers(equipment: Equipment): AemsValidationError | null {
    if (
      equipment?.equipmentTypeParentCode === 'CONT' &&
      equipment.isTaggedForRedelivery === true &&
      equipment.equipmentIsActive === false &&
      !this.isRedeliveryShop(this.selectedShop)
    ) {
      return { equipmentDetails: { error: this.appTextSvc.RedeliveryContainersNotAllowed } };
    }
    return null;
  }

  private isRedeliveryShop(shop: Shop | null): boolean {
    if (shop === null) {
      return false;
    }
    return shop.locationCode?.toUpperCase().startsWith('NEAGA') === true;
  }

  /**
   *
   * @param equipment Method to make sure that all the equipmnets are of same size.
   */
  private validateEquipmentSize(equipment: Equipment): AemsValidationError | null {
    const equipments = this.sharedDataService.ValdatedEquipmentsSnapShot;
    const allSameSize = equipments.every((eq) => eq.equipmentSize === equipment.equipmentSize);
    if (!allSameSize) {
      return { equipmentDetails: { error: this.appTextSvc.equipmentSizeMismatch(equipment.equipmentSize, equipments[0].equipmentSize) } };
    } else {
      return null;
    }
  }
}
